import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Signal,
    signal,
    viewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    DialogService,
    ShCheckboxComponent,
    ShIconComponent,
    ShPaginationComponent,
    ShSpinnerComponent,
    ShTableCellComponent,
    ShTableComponent,
    ShTableHeadComponent,
    ShTableRowComponent,
    ShTooltipComponent
} from '@gbr-monorepo/gbr-shui';
import {
    ApplicationStatus,
    CommentFilter,
    Metadata,
    Season,
    SeasonStatus,
    UserApplication
} from '@gbr-monorepo/interfaces';
import { TranslateModule } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { filter, switchMap, tap } from 'rxjs';
import { ApplicationStatusBadgeComponent } from 'shared-ui';
import { environment } from '../../../../../environments/environment';
import { UserApplicationItem } from '../../../../shared/interfaces/ApplicationItem';
import { CustomCurrencyPipe } from '../../../../shared/pipes/custom-currency/custom-currency.pipe';
import { KebabCasePipe } from '../../../../shared/pipes/kebab-case/kebab-case.pipe';
import { FinancialOverviewResponse } from '../../../models/season-financial.model';
import { LoadApplicationsFilterOptions } from '../../../state/applications-filter/applications-filter.action';
import { ApplicationsFilterState } from '../../../state/applications-filter/applications-filter.state';
import {
    DeleteApplicationComment,
    LazyLoadApplications,
    LoadApplications,
    ToggleSelectedForPayout,
    UpdateApplicationComment
} from '../../../state/season/season.action';
import { SeasonState } from '../../../state/season/season.state';
import { CommentDialogComponent } from '../comment-dialog/comment-dialog.component';
import { ApplicationFilter } from './applications-filter/application-filter.model';
import { ApplicationsFilterComponent } from './applications-filter/applications-filter.component';
@Component({
    selector: 'app-season-applications-table',
    templateUrl: './applications-table.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        ShPaginationComponent,
        CustomCurrencyPipe,
        KebabCasePipe,
        ApplicationsFilterComponent,
        ShCheckboxComponent,
        ShIconComponent,
        ShSpinnerComponent,
        ShTableComponent,
        ShTableRowComponent,
        ShTableHeadComponent,
        ShTableCellComponent,
        ShTooltipComponent,
        ApplicationStatusBadgeComponent
    ]
})
export class SeasonApplicationsTableComponent {
    private readonly store = inject(Store);
    private readonly dialog = inject(DialogService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly table = viewChild.required<ElementRef>('table');

    protected season: Signal<Season> = select(SeasonState.getSeason);
    protected isLoadingApplications: Signal<boolean> = select(SeasonState.isLoadingApplications);
    protected financialOverviewSignal: Signal<FinancialOverviewResponse> = select(SeasonState.getFinancialOverview);
    protected applicationsSignal: Signal<UserApplicationItem[]> = select(SeasonState.getApplications);
    protected pageSize: Signal<number> = select(SeasonState.getApplicationsPageSize);
    protected allApplicationsLoadedSignal: Signal<boolean> = select(SeasonState.allApplicationsLoaded);
    private applicationsMetadata: Signal<Metadata> = select(SeasonState.getApplicationsMetadata);

    protected pageIndex = signal<number>(0);
    protected isPageLoading = signal<boolean>(false);
    protected applicationFilters = signal<ApplicationFilter>({} as ApplicationFilter);
    protected payoutPeriodOptions: Signal<string[]> = select(ApplicationsFilterState.getPayoutPeriodOptions);
    protected payoutPeriodOptionsLoading: Signal<boolean> = select(ApplicationsFilterState.isLoadingOptions);

    protected applicationPotentialPayoutsSignal: Signal<(number | undefined)[]> = computed(() =>
        this.applicationsSignal().map(application => application.potentialPayout)
    );

    protected availableSeasonBudgetSignal: Signal<number> = computed(
        () => this.financialOverviewSignal()?.availableBudget
    );

    protected totalItems = computed(() => this.applicationsMetadata()?.itemCount ?? 0);
    protected totalPages = computed(() => {
        const metadata = this.applicationsMetadata();

        if (!metadata) {
            return 1;
        }

        const lastPageIndex = Number(metadata.lastPage);
        if (Number.isFinite(lastPageIndex) && lastPageIndex >= 0) {
            return Math.max(1, lastPageIndex + 1);
        }

        if (metadata.hasNextPage) {
            const current = Number(metadata.currentPage ?? 0);
            return current <= 0 ? 2 : current + 1;
        }

        const size = this.pageSize() || 1;
        return Math.max(1, Math.ceil(this.totalItems() / size));
    });

    protected applicationSearchControl = new FormControl('');

    protected seasonStatus = SeasonStatus;
    protected applicationStatus = ApplicationStatus;
    protected readonly statusColor = {
        [ApplicationStatus.DRAFT]: '#00BEDC',
        [ApplicationStatus.SUBMITTED]: '#00BEDC',
        [ApplicationStatus.APPROVED]: '#01D65A',
        [ApplicationStatus.DECLINED]: '#FF2640',
        [ApplicationStatus.CANCELED]: '#FF2640'
    };
    protected readonly applicationFilterStates = [
        ApplicationStatus.APPROVED,
        ApplicationStatus.DECLINED,
        ApplicationStatus.SUBMITTED
    ];
    protected readonly applicationFilterComments = Object.values(CommentFilter);
    protected readonly columns = [
        { name: 'seasons.season-details.table.gid', width: '2' },
        { name: 'seasons.season-details.table.name', width: '3' },
        { name: 'seasons.season-details.table.application-status', width: '3' },
        { name: 'seasons.season-details.table.monthly-salary', width: '3' },
        { name: 'seasons.season-details.table.grant-type', width: '2' },
        { name: 'seasons.season-details.table.payout-period', width: '2' },
        { name: 'seasons.season-details.table.children', width: '2' },
        { name: 'seasons.season-details.table.spouse', width: '2' },
        { name: 'seasons.season-details.table.last-year-payout', width: '2' },
        { name: 'seasons.season-details.table.evaluation-points', width: '2' },
        { name: 'seasons.season-details.table.potential-payout', width: '2' },
        { name: 'seasons.season-details.table.comment', width: '2' },
        { name: 'seasons.season-details.table.selected-for-payout', width: '2' }
    ];

    constructor() {
        this.setupApplicationFiltersEffect();
        this.setupFilterOptionsEffect();
    }

    protected toggleSelectedForPayout(application: UserApplication): void {
        this.store.dispatch(new ToggleSelectedForPayout({ application }));
    }

    protected openApplication(application: UserApplication): void {
        window.open(
            `${environment.customerAppUrl}/dashboard/application-process/${application.id}?view=true`,
            '_blank'
        );
    }

    protected openCommentDialog(application: UserApplication): void {
        const dialogRef = this.dialog.open<string, UserApplication>(CommentDialogComponent, {
            data: application,
            width: '500px'
        });

        dialogRef.afterClosed
            .pipe(
                filter(comment => comment !== undefined && comment !== null),
                switchMap(comment =>
                    this.store.dispatch(
                        comment === ''
                            ? new DeleteApplicationComment({ id: application.id })
                            : new UpdateApplicationComment({ id: application.id, comment })
                    )
                )
            )
            .subscribe();
    }

    protected onPaginationMove(event: Event): void {
        const currentPage = Number((event.target as { currentPage?: number | string } | null)?.currentPage ?? 1);
        const pageIndex = Math.max(0, currentPage - 1);
        this.pageIndex.set(pageIndex);
        this.loadApplicationsPage(this.pageIndex(), this.pageSize());
    }

    private setupApplicationFiltersEffect(): void {
        let debounceTimer: NodeJS.Timeout;
        let isInitialLoad = true;

        effect(() => {
            this.applicationFilters();

            if (!this.season()?.id) {
                return;
            }

            clearTimeout(debounceTimer);

            if (isInitialLoad) {
                isInitialLoad = false;
                this.loadApplications();
            } else {
                debounceTimer = setTimeout(() => {
                    this.loadApplications();
                }, 750);
            }
        });

        this.destroyRef.onDestroy(() => clearTimeout(debounceTimer));
    }

    private setupFilterOptionsEffect(): void {
        effect(() => {
            const seasonId = this.season()?.id;

            if (!seasonId) {
                return;
            }

            this.store.dispatch(new LoadApplicationsFilterOptions({ seasonId }));
        });
    }

    private loadApplications(): void {
        this.store.dispatch(
            new LoadApplications({
                filters: this.applicationFilters(),
                seasonId: this.season().id,
                pageSize: this.pageSize()
            })
        );
    }

    private loadApplicationsPage(pageIndex: number, pageSize: number): void {
        this.isPageLoading.set(true);
        const season = this.store.selectSnapshot(SeasonState.getSeason);
        this.store
            .dispatch(
                new LazyLoadApplications({
                    seasonId: season.id,
                    page: pageIndex,
                    pageSize: pageSize
                })
            )
            .pipe(tap(() => this.isPageLoading.set(false)))
            .subscribe();
    }
}
